import { apiRequest } from '../api';

// A client account's type, group and description (batch 5 round 4, COA-6 mechanism / COA-11) — the
// shapes server/src/services/accountingSetup.ts returns, field for field. The chart itself (with
// account_type / account_group / description / kind / normalSide on every row) still comes from
// api.acctChart; adding and editing still go through api.acctAddAccount / api.acctUpdateAccount.
// ⛔ The screen never decides what a type word is or whether it is allowed: `kind` and every refusal
// come from the server's chart rules (server/src/services/coaRules.ts).

/** '' = the account has not been classified yet. */
export type ChartKind = '' | 'header' | 'control' | 'contra' | 'system' | 'reference' | 'posting' | 'unknown';
// (Batch 8 R1) 'house-template' (batch 5's freeze on every template copy) is gone with the account-code literals; its place is
// 'role-on-nonposting' — an account holding a ROLE may not become a type nothing is posted to (server CHART_RETYPE_RULES).
// (R1 ship gate) 'role-on-contra': nor a Contra type.
export type ChartRetypeRule = 'unknown-type' | 'control-frozen' | 'posted-to-nonposting' | 'role-on-nonposting' | 'role-on-contra' | 'header-needs-children'
  | 'posted-control' | 'control-needs-role' | 'control-on-nonposting' | 'control-on-contra' | 'type-class';
/** b13 S2: the tree rules (server/src/services/coaRules.ts CHART_TREE_RULES) and removeAccount's has-children. Each has a
 *  coat_why_<rule> sentence (src/i18n/mt/coatype.ts). */
export type ChartTreeRule = 'parent-not-found' | 'parent-self' | 'parent-cycle' | 'parent-class' | 'parent-kind'
  | 'header-needs-children' | 'header-needs-postable';
export type ChartRule = ChartRetypeRule | ChartTreeRule | 'has-children';

/** b13 S2 (B13-13): which accounts an account may sit under — the server simulated each one through the tree rules, so
 *  the select offers exactly what PATCH { parentId } takes. `movable` = the account cannot leave its parent at all (the
 *  last sub-account of a Header): the screen shows that sentence instead of a select. */
export type ParentOptions = {
  cls: string; current: string;
  movable: { rule: ChartRule; message: string } | null;
  /** '' (the top level) first when allowed, then the possible parents in the chart's tree order. */
  options: { id: string; code: string; nameTh: string; nameEn: string; depth: number; kind: ChartKind }[];
};

export type ChartVocabulary = {
  /** The sixteen type words, in the customer's order, each with what it is. Never empty. */
  types: { word: string; kind: ChartKind }[];
  /** The master template's Subgroup words, then the ones this client already uses. */
  groups: string[];
  masterGroups: string[];
  clientGroups: string[];
};
export type TypeOptions = {
  cls: string; lines: number; templated: boolean; liveChildren: number;
  /** '' first (not classified), then the sixteen words — each with the rule that refuses it, if one does. */
  options: { word: string; kind: ChartKind; refused: ChartRetypeRule | ChartTreeRule | null; message: string }[];
  /** Which classes each word may stand on (for the "only on …" reason). */
  classesOf: Record<string, string[]>;
};

/** Batch 8 R1 (design §3.1 / §3.2): an account's ROLE — what the system itself posts to it for (services/coaRules.ts
 *  COA_ROLES). The chart's rows carry `role` (its own), `roleEffective` / `roleSource` (the role it holds: its own 'set',
 *  or its house-template row's 'template'), `roleClasses` and `roleMapped` (the payroll / WHT posting maps' answers, read
 *  only). The rules a role is refused by (services/coaRules.ts ROLE_RULES), each with a coar_why_<rule> sentence
 *  (src/i18n/mt/coarole.ts). */
export type RoleRule = 'unknown-role' | 'role-template' | 'role-pinned' | 'role-deleted' | 'role-inactive' | 'role-control' | 'role-nonposting'
  | 'role-contra' | 'role-class' | 'role-cash' | 'role-mapped' | 'role-fixed-asset' | 'role-taken';
/** (R1 review) The rules a role LEAVING an account is refused by (services/coaRules.ts ROLE_LEAVE_RULES): the account holds
 *  another role from the template (role-inherited — second pass), the account it would go back to (the template's copy)
 *  cannot hold it (role-fallback), or no account would (role-orphaned). */
export type RoleLeaveRule = 'role-inherited' | 'role-orphaned' | 'role-fallback';
/** (R1 review, second pass) The rule a role MOVE is refused by (services/coaRules.ts ROLE_MOVE_RULES): generated drafts still
 *  carry a line on the account it leaves. */
export type RoleMoveRule = 'role-drafts';
/** One move a choice makes: the role, the account it leaves and the one it goes to (codes), the posted lines left behind,
 *  and the generated drafts (their numbers) that refuse it. */
export type RoleMoveInfo = { role: string; from: string; to: string; posted: number; drafts: string[];
  /** (R1 re-verify, v3) The same on the account the role goes to: its own posted lines, and the generated drafts there that
   *  refuse the move. `from` is '' when no account holds the role now. */
  toPosted?: number; toDrafts?: string[] };
/** What role-cash / role-mapped / (ship gate) role-fixed-asset weighed (services/coaRules.ts RoleFacts). */
export type RoleFacts = { cash?: { why: 'cash' | 'bank' | 'box'; name: string } | null;
  /** (R1 re-verify, v3) …an employee's own salary expense account, a payroll run not posted yet, and the per-charge memories
   *  (a purchase charge, a sale charge, a sale's pass-through role, a supplier) too; `name` what a sentence names. */
  mapped?: { source: 'payroll' | 'payroll_item' | 'settlement' | 'employee' | 'payroll_run' | 'expense_memory' | 'revenue_memory' | 'sale_role_memory' | 'vendor_memory';
    role: string; words: string; name?: string } | null;
  asset?: { source: 'category' | 'asset' | 'disposal'; what: 'cost' | 'accum' | 'expense' | 'disposal'; name: string } | null };
/** What a leave rule names: the role the account would lose, and the copy it would go back to with the role rule it breaks. */
export type RoleLeaves = { role: string; fallback: { code: string; rule: RoleRule } | null } | null;
/** GET /chart/role-options: every role, each with the rule that would refuse it on THIS account (for the class `cls` the
 *  Edit form says) and the account holding it now. Asked only for a login the server says may change a role
 *  (canEditRole — an administrator, D1). `none`: taking the account's own role off ('' in the select). */
export type RoleOptions = {
  current: string; effective: string; source: '' | 'set' | 'template'; cls: string; type?: string; canEditRole: boolean;
  options: { role: string; label: string; classes: string[]; tax: boolean;
    heldBy: { id: string; code: string; source: 'set' | 'template' } | null; refused: RoleRule | RoleLeaveRule | RoleMoveRule | null; message: string; leaves?: RoleLeaves;
    moves?: RoleMoveInfo[]; warning?: string; facts?: RoleFacts | null;
    /** (R1 ship gate) role-pinned: the account the pinned role stays on (its template copy), or null. */
    pinnedTo?: { code: string } | null }[];
  none?: { refused: RoleLeaveRule | RoleMoveRule | null; message: string; leaves: RoleLeaves; moves?: RoleMoveInfo[]; warning?: string };
};

export const coaTypeApi = {
  vocabulary: (clientId: string): Promise<ChartVocabulary> =>
    apiRequest('GET', `/accounting/chart/vocabulary?clientId=${encodeURIComponent(clientId)}`),
  /** For an existing account (`accountId`), or a new one of class `cls`. `cls` may also be what an
   *  edit form now says, when it differs from the stored class. */
  typeOptions: (clientId: string, q: { accountId?: string; cls?: string; role?: string }): Promise<TypeOptions> => {
    const qs = new URLSearchParams({ clientId });
    if (q.accountId) qs.set('accountId', q.accountId);
    if (q.cls) qs.set('cls', q.cls);
    // (R1 ship gate) the role the Edit form now says ('' = none of its own) — sent only when the form can say one.
    if (q.accountId && q.role !== undefined) qs.set('role', q.role);
    return apiRequest('GET', `/accounting/chart/type-options?${qs.toString()}`);
  },
  /** b13 S2: the Parent select's choices — for an existing account (`accountId`, with the class its form now says), or a
   *  new one of class `cls`. */
  /** Batch 8 R1: which role one account may be given — for the class its Edit form now says (`cls`) and (R1 ship gate) the
   *  type it now says (`type`, '' = not classified; absent = the stored one). */
  roleOptions: (clientId: string, accountId: string, cls?: string, type?: string): Promise<RoleOptions> => {
    const qs = new URLSearchParams({ clientId, accountId });
    if (cls) qs.set('cls', cls);
    if (type !== undefined) qs.set('type', type);
    return apiRequest('GET', `/accounting/chart/role-options?${qs.toString()}`);
  },
  parentOptions: (clientId: string, q: { accountId?: string; cls?: string }): Promise<ParentOptions> => {
    const qs = new URLSearchParams({ clientId });
    if (q.accountId) qs.set('accountId', q.accountId);
    if (q.cls) qs.set('cls', q.cls);
    return apiRequest('GET', `/accounting/chart/parent-options?${qs.toString()}`);
  },
};
